import React from "react";
import { useTranslation } from "react-i18next";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import { getStudyMaterialLink, sortStudyMaterials, studyMaterials, STUDY_MATERIAL_PRICES_CHECKED_ON, StudyMaterialSort } from "../../consts/studyMaterials";
import styles from "./studyMaterialsPage.module.scss";
import PageLanguageLink from "../PageLanguageLink/PageLanguageLink";
import JlptNotice from "../JlptNotice/JlptNotice";

// Amazon Associates Operating Agreement (Amazon.es, English version), Clause 5:
// the statement below must be shown "clearly and prominently" wherever
// Program Content / Special Links are used. It is kept in Amazon's own English
// wording on both language versions (lang="en"); the plain-language line next
// to it carries the meaning in the page's language. Any page that renders a
// link from getStudyMaterialLink() with isAffiliate must show this too.
function AffiliateDisclosure() {
    const { t } = useTranslation("studyMaterialsPage");
    return (
        <div className={styles.disclosure} role="note" aria-labelledby="affiliate-disclosure-title">
            <p id="affiliate-disclosure-title" className={styles.disclosureTitle}>{t("affiliate.title")}</p>
            <p className={styles.disclosureStatement} lang="en">{t("affiliate.statement")}</p>
            <p className={styles.disclosureText}>{t("affiliate.explanation")}</p>
        </div>
    );
}

function BookCover({ material }: { material: (typeof studyMaterials)[number] }) {
    const { t } = useTranslation("studyMaterialsPage");
    if (material.coverImage) {
        return (
            <img
                className={styles.bookCoverImage}
                src={material.coverImage.src}
                alt={t(`materials.${material.id}.coverAlt`, { defaultValue: material.coverImage.alt })}
                loading="lazy"
            />
        );
    }

    return (
        <div className={styles.bookCover} role="img" aria-label={t("placeholderCoverAlt", { title: material.title })}>
            <span className={styles.coverTurtle} aria-hidden="true">🐢</span>
            <span className={styles.coverSeries}>GENKI I</span>
            <span className={styles.coverType}>{t(`materials.${material.id}.type`, { defaultValue: material.type })}</span>
        </div>
    );
}

function StudyMaterialsPage() {
    const { t, i18n } = useTranslation("studyMaterialsPage");
    const [sortOrder, setSortOrder] = React.useState<StudyMaterialSort>("recommended");
    const isPt = i18n.language === "pt";
    const switchPath = isPt ? "/study-materials" : "/pt/study-materials";
    const currentPath = isPt ? "/pt/study-materials" : "/study-materials";
    const sortedMaterials = sortStudyMaterials(studyMaterials, sortOrder);
    const priceCheckedOn = new Intl.DateTimeFormat(isPt ? "pt-PT" : "en-GB", {
        day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
    }).format(new Date(`${STUDY_MATERIAL_PRICES_CHECKED_ON}T00:00:00Z`));
    const formatPrice = (price: number) => new Intl.NumberFormat(isPt ? "pt-PT" : "en-IE", {
        style: "currency", currency: "EUR",
    }).format(price);

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />

            <main id="main-content" className={styles.main}>
                <section className={styles.container} aria-labelledby="study-materials-title">
                    <p className={styles.eyebrow}>{t("eyebrow")}</p>
                    <h1 id="study-materials-title" className={styles.title}>{t("title")}</h1>
                    <AffiliateDisclosure />
                    <p className={styles.introduction}>{t("introduction")}</p>
                    <div className={styles.sortControls}>
                        <label htmlFor="study-materials-sort">{t("sortBy")}</label>
                        <select
                            id="study-materials-sort"
                            value={sortOrder}
                            onChange={(event) => setSortOrder(event.target.value as StudyMaterialSort)}
                        >
                            <option value="recommended">{t("sortRecommended")}</option>
                            <option value="price-ascending">{t("sortPriceAscending")}</option>
                            <option value="price-descending">{t("sortPriceDescending")}</option>
                        </select>
                        <p className={styles.priceNote}>
                            {t("priceInformation", { date: priceCheckedOn })}
                        </p>
                    </div>
                    <JlptNotice variant="labels" />
                    <div className={styles.materialList}>
                        {sortedMaterials.map((material) => {
                            const link = getStudyMaterialLink(material, "materialsPage");

                            return (
                                <article key={material.id} className={styles.materialCard}>
                                    <BookCover material={material} />
                                    <div className={styles.cardContent}>
                                        <div className={styles.typeRow}>
                                            <p className={styles.resourceType}>{t(`materials.${material.id}.type`, { defaultValue: material.type })}</p>
                                            {material.jlptLevel && (
                                                <span className={styles.jlptBadge}>{t("goodFor", { level: material.jlptLevel })}</span>
                                            )}
                                        </div>
                                        <h2 className={styles.materialTitle}>{material.title}</h2>
                                        <p className={styles.description}>{t(`materials.${material.id}.description`, { defaultValue: material.description })}</p>
                                        <dl className={styles.details}>
                                            <div>
                                                <dt>{t("suitableFor")}</dt>
                                                <dd>{t(`materials.${material.id}.suitableFor`, { defaultValue: material.suitableFor })}</dd>
                                            </div>
                                            <div>
                                                <dt>{t("whyRecommended")}</dt>
                                                <dd>{t(`materials.${material.id}.recommendation`, { defaultValue: material.recommendation })}</dd>
                                            </div>
                                        </dl>
                                        <dl className={styles.bookDetails} aria-label={t("editionDetailsLabel", { title: material.title })}>
                                            {material.details?.map((detail) => (
                                                <div key={detail.label}>
                                                    <dt>{t(`detailLabels.${detail.label}`, { defaultValue: detail.label })}</dt>
                                                    <dd>{t(`detailValues.${detail.value}`, { defaultValue: detail.value })}</dd>
                                                </div>
                                            ))}
                                            <div>
                                                <dt>{t("detailLabels.Price", { defaultValue: "Price" })}</dt>
                                                <dd>{formatPrice(material.price)}</dd>
                                            </div>
                                        </dl>
                                        {link.href ? (
                                            <a
                                                className={styles.action}
                                                href={link.href}
                                                target="_blank"
                                                rel={link.isAffiliate ? "sponsored noopener noreferrer" : "noopener noreferrer"}
                                                data-tracking-id={link.trackingId || undefined}
                                            >
                                                {t(`materials.${material.id}.ctaLabel`, { defaultValue: link.ctaLabel })}<span className={styles.actionArrow} aria-hidden="true">→</span>
                                            </a>
                                        ) : (
                                            <button type="button" className={styles.action} disabled title={t("linkComingSoonTitle")}>
                                                {t("viewBook")} <span className={styles.comingSoon}>{t("linkComingSoon")}</span>
                                            </button>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                    <PageLanguageLink to={switchPath} />
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default StudyMaterialsPage;
